import type { ReactNode } from 'react';

import { cn } from '@/lib/shadcn/utils';

/**
 * Small single-use pieces of the signup steps: fields, the action row and
 * the status dot. Larger pieces live beside this file (`SignupFlowShell`,
 * `StepHeader`, `StepTransition`, `ChoiceList`); `buttonClass` in `motion.ts`.
 */

interface FieldProps {
  label: string;
  htmlFor?: string;
  hint?: string;
  /** The asterisk is hidden from the accessible name; controls carry `aria-required`. */
  required?: boolean;
  error?: string;
  children: ReactNode;
}

export const Field = ({
  label,
  htmlFor,
  hint,
  required,
  error,
  children,
}: FieldProps) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={htmlFor} className="text-[13px] font-medium">
      {label}
      {required && (
        <span className="text-destructive ml-0.5" aria-hidden>
          *
        </span>
      )}
      {hint && (
        <span className="text-muted-foreground ml-1.5 font-normal">{hint}</span>
      )}
    </label>
    {children}
    {error && (
      <p className="text-destructive text-[13px]" role="alert">
        {error}
      </p>
    )}
  </div>
);

interface StepActionsProps {
  children: ReactNode;
}

/** Right-aligned on desktop, stacked full-width on phones. */
export const StepActions = ({ children }: StepActionsProps) => (
  <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3">
    {children}
  </div>
);

interface StatusDotProps {
  tone: 'ok' | 'neutral';
  children: ReactNode;
}

/** The portal's status idiom: a dot plus text, not a pill. */
export const StatusDot = ({ tone, children }: StatusDotProps) => (
  <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs font-medium">
    <span
      className={cn(
        'size-1.5 rounded-full',
        tone === 'ok' ? 'bg-semantic-560' : 'bg-neutral-500',
      )}
    />
    {children}
  </span>
);
